import { useState } from "react";
import axios from "axios";
import { SIGNUP } from "@/constants/testIds";
import { Check, Loader2, Zap, BadgePercent, Package, Mail } from "lucide-react";

/* eslint-disable react/no-unescaped-entities */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BENEFITS = [
  { icon: Zap, label: "Early Access", body: "Order before the public launch." },
  { icon: BadgePercent, label: "Founder Pricing", body: "Locked-in pricing for our first supporters." },
  { icon: Package, label: "Limited First Batch", body: "Only our first batch carries the founder seal." },
  { icon: Mail, label: "Exclusive Updates", body: "Origin stories and small-batch news." },
];

export const Signup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/launch-signup`, {
        name: name.trim(),
        email: email.trim(),
        source: "landing",
      });
      setSuccess(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setError("You're already on the list — thank you!");
      } else if (status === 422) {
        setError("Please enter a valid email address.");
      } else {
        setError("Something went wrong. Please try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="signup"
      data-testid={SIGNUP.wrapper}
      className="relative py-24 sm:py-32 bg-[#fdfaf4]"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="relative bg-white rounded-[2.5rem] p-7 sm:p-12 lg:p-16 shadow-[0_24px_70px_rgba(26,54,38,0.1)] border border-[#c5a059]/15 overflow-hidden">
          <div className="absolute top-0 left-0 w-24 h-24 border-t border-l border-[#c5a059]/30 rounded-tl-[2.5rem]" />
          <div className="absolute bottom-0 right-0 w-24 h-24 border-b border-r border-[#c5a059]/30 rounded-br-[2.5rem]" />

          {!success ? (
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start relative">
              <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-[#c5a059]" />
                  <span className="overline">Launch List</span>
                </div>
                <h2 className="font-serif-display text-[36px] sm:text-[52px] leading-[1.05] text-[#1a3626] tracking-tight">
                  Be First When <em className="italic text-[#8b5a2b]">We Launch.</em>
                </h2>
                <p className="mt-5 text-[15px] sm:text-[16px] leading-[1.75] text-[#5c4033] max-w-lg">
                  Join a small circle of early supporters who'll be the first
                  to taste our debut batch — and help shape what comes next.
                </p>

                <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                  {BENEFITS.map((b, i) => {
                    const Icon = b.icon;
                    return (
                      <li
                        key={i}
                        data-testid={SIGNUP.benefit(i)}
                        className="flex items-start gap-3 rounded-2xl bg-[#fdfaf4] border border-[#c5a059]/20 px-4 py-3.5"
                      >
                        <div className="w-9 h-9 rounded-full bg-[#1a3626] text-[#c5a059] flex items-center justify-center shrink-0">
                          <Icon size={15} />
                        </div>
                        <div>
                          <div className="text-[13px] font-semibold text-[#1a3626]">
                            {b.label}
                          </div>
                          <div className="text-[12px] text-[#5c4033] leading-[1.5] mt-0.5">
                            {b.body}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="lg:col-span-6">
                <form
                  data-testid={SIGNUP.form}
                  onSubmit={handleSubmit}
                  className="grid gap-4 bg-[#faf7f2] rounded-[2rem] p-6 sm:p-8 border border-[#c5a059]/15"
                >
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#8b5a2b]">
                      Name
                    </span>
                    <input
                      data-testid={SIGNUP.name}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      autoComplete="name"
                      className="naija-input mt-2 w-full bg-white border border-[#1a3626]/12 rounded-2xl px-5 py-4 text-[15px] text-[#1a3626] placeholder:text-[#1a3626]/40 transition-all"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#8b5a2b]">
                      Email
                    </span>
                    <input
                      data-testid={SIGNUP.email}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="naija-input mt-2 w-full bg-white border border-[#1a3626]/12 rounded-2xl px-5 py-4 text-[15px] text-[#1a3626] placeholder:text-[#1a3626]/40 transition-all"
                    />
                  </label>

                  {error && (
                    <div
                      data-testid={SIGNUP.error}
                      className="text-[13px] text-[#c86b5e] bg-[#c86b5e]/8 border border-[#c86b5e]/25 rounded-xl px-4 py-3"
                    >
                      {error}
                    </div>
                  )}

                  <button
                    data-testid={SIGNUP.submit}
                    type="submit"
                    disabled={loading}
                    className="mt-1 inline-flex items-center justify-center gap-2 bg-[#1a3626] text-[#faf7f2] rounded-full px-7 py-4 text-[14px] font-medium hover:bg-[#12261b] transition-all duration-300 shadow-[0_10px_30px_rgba(26,54,38,0.22)] disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Adding you…
                      </>
                    ) : (
                      <>
                        Reserve Your Spot
                        <span className="text-[#c5a059]">→</span>
                      </>
                    )}
                  </button>

                  <p className="text-center text-[11px] text-[#5c4033]/70 tracking-wide">
                    No spam. Unsubscribe any time.
                  </p>
                </form>
              </div>
            </div>
          ) : (
            <div data-testid={SIGNUP.success} className="text-center py-6 relative">
              <div className="w-16 h-16 rounded-full bg-[#1a3626] text-[#c5a059] mx-auto flex items-center justify-center shadow-[0_8px_24px_rgba(26,54,38,0.22)]">
                <Check size={26} strokeWidth={2.5} />
              </div>
              <div className="mt-6 overline">Welcome to NAIJA</div>
              <h3 className="mt-3 font-serif-display text-[32px] sm:text-[44px] leading-[1.08] text-[#1a3626]">
                Thank you — you are on the <br className="hidden sm:block" />
                <em className="italic text-[#8b5a2b]">NAIJA Cashews launch list.</em>
              </h3>
              <p className="mt-5 text-[15px] text-[#5c4033] max-w-md mx-auto">
                We'll write to you the moment our first batch is ready to ship
                across Europe. Founder pricing locked in. Welcome to the
                origin story.
              </p>
              <button
                onClick={() => {
                  setSuccess(false);
                  setName("");
                  setEmail("");
                }}
                className="mt-8 inline-flex items-center gap-2 border border-[#1a3626]/20 text-[#1a3626] rounded-full px-6 py-3 text-[13px] hover:bg-[#1a3626]/5"
              >
                Add another email
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
